import { Dispatch, DragEvent, SetStateAction, useEffect, useState } from "react";
import styles from "./AddMultiplePlaces.module.css";
import { ContentList } from "../../../../screens/MapScreen";
import AddPlacesFileItem from "./AddPlacesFileItem";
import ContentTitle from "../ContentTitle";
import Icon from "../../../common/Icon";
import {
    CategoryOptions,
    downloadCSV,
    JSONArrayToCSV,
    MetaData,
    path,
    PlaceCoords,
    PlaceCoordsList,
    PlaceData,
    PlaceProps,
    PlacePropsList,
} from "../../../common/Common";
// import { properties as dummyProperties, geocoding as geocodingDummy } from "../../../../dummy/multiplePlaces";
import DataProvider from "../../../../data_provider/DataProvider";
import { useSearchParams } from "react-router-dom";
import MiniButton, { MiniButtonType } from "../../MiniButton";
import BottomButtonBox from "../../BottomButtonBox";

import { useModal } from "../../../modal/Modal";
import ModalButtons from "../../../modal/ModalButtons";
import CsvImporter from "../../../common/CsvImporter";
import { XLSXImporter } from "../../../common/XLSXImporter";
import { isCatchClause } from "typescript";

interface AddMultiplePlacesProps {
    changeContent: (type: ContentList, reset?: boolean) => void;
    progress: (num?: number) => number;

    coords: PlaceCoordsList[];
    addResult: (coords: PlaceCoordsList) => void; // 지오코딩 결과 저장
    deleteResult: (fileIdx?: number, reset?: boolean) => void; // 파일 삭제 시 지오코딩 결과 함께 삭제

    selectedPoiIdx?: string;
    setSelectedPlace?: Dispatch<SetStateAction<PlaceData | undefined>>;

    //hmchoi
    categories: CategoryOptions[];
}

export interface FileListItem {
    file: File;
    data?: PlacePropsList; // 파싱된 데이터 -> 지오코딩 성공한 데이터
    result?: {
        // 지오코딩 결과(개수)
        success: number;
        fail: number;
    };
}

/**
 * 신규 장소 등록 - 일괄 추가 화면 화면
 * @author mjkang
 * @date 2024-09-12 13:12:37
 * @param props: AddMultiplePlacesProps
 * @props changeContent: 사이드 시트 콘텐츠를 변경하는 함수
 *        progress: 지오코딩 진행상황(%) getter/setter
 *        setSelectedIdx: 선택한 장소 인덱스 setter
 */
const AddMultiplePlaces = (props: AddMultiplePlacesProps) => {
    const { ModalComponent, openModal, closeModal } = useModal();

    const [params] = useSearchParams();
    const { addResult, deleteResult } = props;

    const [dragState, setDragState] = useState(false);
    const [metaData, setMetaData] = useState<MetaData | undefined>();

    const [selectedFileIdx, setSelectedFileIdx] = useState(-1);
    const [fileList, setFileList] = useState<FileListItem[]>([]);
    const [failedList, setFailedList] = useState<PlacePropsList[]>([]);

    useEffect(() => {
        reqMetadata();

        return () => {
            reset();
        };
    }, []);

    /*
    useEffect(() => {
        if (selectedFileIdx < 0) {
            reset();
        } else {
            const isNewFile = fileList[selectedFileIdx].result === undefined;

            if (isNewFile) {
                // 새로 추가된 파일일 때
                geocodingTest();
            }
        }
    }, [selectedFileIdx]);
    */

    //hmchoi
    useEffect(() => {
        if (selectedFileIdx < 0) {
            reset();
            return;
        }

        const selectedFile = fileList[selectedFileIdx];

        /**
         * 아직 setFileList 결과가 반영되지 않은 경우 방어
         */
        if (!selectedFile) {
            return;
        }

        /**
         * result가 이미 있으면
         * 이미 지오코딩이 끝난 파일이므로 다시 호출하지 않음
         */
        if (selectedFile.result !== undefined) {
            return;
        }

        geocoding(selectedFileIdx);
    }, [selectedFileIdx, fileList.length]);

    useEffect(() => {
        const mapid = params.get("id");
        if (!mapid) return;

        setSelectedPlaceData(mapid);
    }, [props.selectedPoiIdx]);

    const setSelectedPlaceData = async (mapid: string) => {
        if (props.selectedPoiIdx && props.setSelectedPlace) {
            const [fileIdx, placeIdx] = props.selectedPoiIdx.split("_");
            const placeList = fileList[Number(fileIdx)];

            if (placeList.data && placeList.result && metaData) {
                const { placeName, mainData, imageField, extraData, fieldName } = metaData.metadata;
                const response = await DataProvider.GetPlaceInfo({ mapid, XID: "1", type: "1" });
                if (!response.flag || !response.result) return;

                const place = placeList.data[Number(placeIdx)];
                const emptyData = {
                    fieldKey: "",
                    fieldName: "",
                    value: "",
                };

                const _placeData: PlaceData = {
                    address: { ...emptyData },
                    category: { ...emptyData },
                    placeName: {
                        fieldKey: placeName,
                        fieldName: fieldName[placeName],
                        value: place[placeName],
                    },
                    mainData: mainData.map((fieldKey) => {
                        const _fieldName = fieldName[fieldKey];
                        return {
                            fieldKey: fieldKey,
                            fieldName: _fieldName,
                            value: place[fieldKey],
                            template: metaData.template ? metaData.template[fieldKey] : undefined,
                            // mjkang 주요 정보 스타일을 인터페이스에 맞게 파싱해야 함
                            // hmchoi 스타일 추가
                            style: metaData.metadata.style ? metaData.metadata.style[fieldKey] : undefined,
                        };
                    }),
                    imageField: imageField
                        ? {
                              fieldKey: imageField,
                              fieldName: fieldName[imageField],
                              value: [
                                  {
                                      src: place[imageField],
                                  },
                              ],
                          }
                        : undefined,
                    extraData: extraData
                        ? extraData.map((fieldKey) => {
                              return {
                                  fieldKey: fieldKey,
                                  fieldName: fieldName[fieldKey],
                                  value: place[fieldKey],
                              };
                          })
                        : undefined,
                };

                props.setSelectedPlace(_placeData);
            }
        }
    };

    const reset = () => {
        props.progress(-1);
        deleteResult(undefined, true);
    };

    const cancel = () => {
        props.changeContent(ContentList.Back, true);
    };

    /**
     * 신규 장소 일괄 등록 확인 버튼 onClick 함수
     */
    /*
    const save = () => {
        // mjkang 장소 일괄 추가 API 연결
        props.changeContent(ContentList.List, true);
    };
    */

    /**
     * 주소 필드 validation API 호출 함수
     */
    const reqMetadata = async () => {
        const mapid = params.get("id");
        if (mapid) {
            const data = await DataProvider.GetMetadata({ mapid });
            if (!data.flag || !data.result) return;
            setMetaData(data.result);
        }
    };

    /**
     * 드랍존 드래그 이벤트 통합 함수
     */
    const handleDrag = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        switch (e.type) {
            case "dragover":
                if (!dragState) {
                    setDragState(true);
                }
                break;
            case "dragleave":
                setDragState(false);
                break;
            case "drop":
                if (e.dataTransfer) {
                    const file = e.dataTransfer.files[0];
                    readFile(file);
                }
                setDragState(false);
                break;
            default:
                break;
        }
    };

    /*
    const readFile = (file: File) => {
        const fileName = file.name.toLocaleLowerCase();

        if (!fileName.endsWith("csv") && !fileName.endsWith("xlsx")) {
            // mjkang xlsm, xlsb 형식 추가 가능 여부 확인 필요
            openModal({
                title: "안내",
                children: "csv 혹은 엑셀 형식 파일만 업로드 가능합니다.",
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
            setDragState(false);
            return;
        }

        const fileIdx = fileList.length; // 새 파일의 인덱스
        const _newFile = {
            file: file,
            data: dummyProperties, // mjkang csv -> PlacePropsList 처리
        };

        setFileList([...fileList, _newFile]);
        setSelectedFileIdx(fileIdx); // 새로 추가한 파일 선택

        props.progress(0);
    };
    */

    /*
    // TOOD: 미구현 사항
    // 개발용 더미 함수
    const geocodingTest = () => {
        let second = 0;

        const progressTest = setInterval(() => {
            ++second;
            props.progress(second);

            if (second == 100) {
                clearInterval(progressTest);
                props.progress(++second);

                // geocoding();

                // 카테고리 개수 체크 해서 모달 띄우도록 수정필요
                openModal({
                    title: "안내",
                    children: (
                        <>
                            <p>이 데이터를 추가하면 카테고리 수가 16개를 초과합니다.</p>
                            <p>신규 장소를 등록할 수 없습니다.</p>
                        </>
                    ),
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Secondary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
            }
        }, 10);

        return () => {
            clearInterval(progressTest);
        };
    };

    const geocoding = () => {
        const _fileList = [...fileList];
        const _data = _fileList[selectedFileIdx].data;

        if (_data && metaData) {
            const _coords: PlaceCoordsList = {};
            const _failedProps: PlacePropsList = {};
            const { geoField, category, placeName } = metaData.metadata;

            Object.keys(_data).forEach((key) => {
                const rowNum = Number(key);

                if (rowNum !== 0) {
                    // fieldName은 skip
                    const _row: PlaceProps = _data[rowNum];
                    const _result = geocodingDummy(_row[geoField]);

                    if (_result) {
                        // 지오코딩 성공 시
                        _coords[rowNum] = {
                            coords: _result,
                            category: _row[category!] ?? null, // FIXME: category null 처리 필요
                            label: _row[placeName],
                        };
                    } else {
                        // 지오코딩 실패 시
                        _failedProps[rowNum] = _row;
                        delete _data[rowNum]; // 성공 데이터만 남기고 실패 데이터는 삭제
                    }
                }
            });

            delete _data[0]; // fieldName row 삭제

            fileList[selectedFileIdx].result = {
                success: Object.keys(_data).length,
                fail: Object.keys(_failedProps).length,
            };

            addResult(_coords);
            setFileList(_fileList);
            setFailedList([...failedList, _failedProps]);
        }
    };
    */
    

    /**
     * 전체 파일에 대한 지오코딩 처리 완료 여부 검사 함수
     * @returns boolean
     */
    const validation = () => {
        const notProcessed = fileList.filter((item) => item.result === undefined);

        return fileList.length > 0 && notProcessed.length === 0;
    };

    /**
     * 도움말 버튼 onClick 함수
     */
    const openGuide = () => {
        window.open(path.GUIDE, "_blank");
    };

    /**
     * 형식 다운로드 버튼 onClick 함수
     */
    const downloadForm = () => {
        if (metaData) {
            const csv = JSONArrayToCSV([metaData.metadata.fieldName]);
            downloadCSV(csv, "format");
        }
    };

    /**
     * 신규 장소 일괄 등록 누적 결과 렌더링 함수
     */
    const renderResult = () => {
        const count = { success: 0, fail: 0 };

        if (fileList.length > 1) {
            fileList.forEach((item) => {
                const result = item.result;
                if (result) {
                    count.success += result.success;
                    count.fail += result.fail;
                }
            });

            return (
                <div className={styles.content}>
                    <span className={styles.title}>{"누적 등록 결과"}</span>
                    <div className={styles.result}>
                        <div className={styles.accumulate}>
                            <div className={styles.row}>
                                <span>{"전체"}</span>
                                <span className={styles.all}>{count.success + count.fail}</span>
                            </div>
                            <div className={styles.row}>
                                <span>{"성공"}</span>
                                <span className={styles.success}>{count.success}</span>
                                <span>{"실패"}</span>
                                <span className={styles.fail}>{count.fail}</span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };
    
    // hmchoi **********************************************************************************
    interface SaveTarget {
        fileIdx: number;
        rowIdx: number;
        data: PlaceProps;
        coords: number[];
    }

    const buildSaveTargets = (): SaveTarget[] => {
        const targets: SaveTarget[] =
            [];

        fileList.forEach(
            (file, fileIdx) => {
                if (!file.data) {
                    return;
                }

                const coords =
                    props.coords[fileIdx];

                if (!coords) {
                    return;
                }

                Object.entries(
                    file.data
                ).forEach(
                    ([rowKey, row]) => {
                        const rowIdx =
                            Number(
                                rowKey
                            );

                        const coord =
                            coords[
                                rowIdx
                            ];

                        if (
                            !coord?.coords
                        ) {
                            return;
                        }

                        targets.push({
                            fileIdx,
                            rowIdx,
                            data: row,
                            coords:
                                coord.coords,
                        });
                    }
                );
            }
        );

        return targets;
    };

    const runWithConcurrency = async <T, >(
        items: T[],
        worker: (
            item: T,
            index: number
        ) => Promise<void>,
        concurrency: number = 3
    ) => {
        let cursor = 0;

        const runner = async () => {
            while (true) {
                const current = cursor++;

                if (current >= items.length) {
                    return;
                }

                await worker(
                    items[current],
                    current
                );
            }
        };

        const workers = Array.from(
            {
                length: Math.min(
                    concurrency,
                    items.length
                ),
            },
            () => runner()
        );

        await Promise.all(workers);
    };

    const createPlaceFormData = (
        row: PlaceProps,
        coords: number[]
    ) => {
        const formData = new FormData();

        Object.entries(row).forEach(
            ([key, value]) => {
                formData.append(
                    key,
                    value ?? ""
                );
            }
        );

        formData.append(
            "geometry",
            coords.join(",")
        );

        return formData;
    };
    
    const save = async () => {
        const mapid =
            params.get("id");

        if (!mapid) {
            return;
        }

        if (isCategoryLimitExceeded()) {
            openModal({
                title: "안내",
                children: (
                    <>
                        <p>이 데이터를 추가하면 카테고리 수가 16개를 초과합니다.</p>
                        <p>신규 장소를 등록할 수 없습니다.</p>
                    </>
                ),
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Secondary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        }
        const targets = buildSaveTargets();

        if (targets.length === 0) {
            return;
        }

        const failed:
            SaveTarget[] = [];

        let completed = 0;

        props.progress(0);

        await runWithConcurrency(
            targets,
            async (target) => {
                try {
                    const formData =
                        createPlaceFormData(
                            target.data,
                            target.coords
                        );

                    const response =
                        await DataProvider.AddPlaceInfo(
                            { mapid },
                            formData
                        );

                    if (!response.flag) {
                        failed.push(
                            target
                        );
                    }
                } catch (error) {
                    console.error(
                        error
                    );

                    failed.push(
                        target
                    );
                } finally {
                    completed++;

                    props.progress(
                        Math.floor(
                            (completed /
                                targets.length) *
                                100
                        )
                    );
                }
            },
            3
        );

        props.progress(101);

        if (failed.length > 0) {
            openModal({
                title: "안내",
                children:
                    `${targets.length}건 중 ` +
                    `${targets.length - failed.length}건 성공, ` +
                    `${failed.length}건 실패했습니다.`,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick:
                                    closeModal,
                            },
                        ]}
                    />
                ),
            });

            return;
        }

        props.changeContent(
            ContentList.List,
            true
        );
    }

    const convertRowsToPlaceProps = (
        rows: string[][],
        metaData: MetaData
    ): PlacePropsList => {
        const headers = rows[0].map((v) => String(v).trim());

        const fieldName = metaData.metadata.fieldName;

        const fieldKeyByName = Object.entries(fieldName).reduce<Record<string, string>>(
            (result, [fieldKey, displayName]) => {
                result[String(displayName).trim()] = fieldKey;
                return result;
            },
            {}
        );

        const fieldKeys = headers.map((header) => {
            // display name 또는 field key 둘 다 허용
            if (fieldName[header] !== undefined) {
                return header;
            }

            return fieldKeyByName[header];
        });

        const invalidHeaders = headers.filter(
            (_, index) => !fieldKeys[index]
        );

        if (invalidHeaders.length > 0) {
            throw new Error(
                `알 수 없는 필드가 있습니다: ${invalidHeaders.join(", ")}`
            );
        }

        const result: PlacePropsList = {};

        rows.slice(1).forEach((row, index) => {
            const props: PlaceProps = {};

            fieldKeys.forEach((fieldKey, columnIndex) => {
                props[fieldKey] = String(row[columnIndex] ?? "").trim();
            });

            // 완전히 빈 행은 제거
            const isEmpty = Object.values(props).every(
                (value) => value.trim() === ""
            );

            if (!isEmpty) {
                result[index] = props;
            }
        });

        return result;
    };
    const parseFile = async (file: File): Promise<string[][]> => {
        const ext = file.name.split(".").pop()?.toLowerCase();

        switch (ext) {
            case "csv":
                return await CsvImporter.csvParser(file);
            case "xlsx":
                const importer = new XLSXImporter(file);
                return await importer.import();
            // xlsm, xlsb 형식 추가 가능 여부 확인 필요
            default:
                openModal({
                    title: "안내",
                    children: "csv 혹은 엑셀 형식 파일만 업로드 가능합니다.",
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
                setDragState(false);
                throw new Error("지원하지 않는 파일 형식입니다.");
        }
    }
    const readFile = async (file: File) => {
        try {
            const rows = await parseFile(file);

            if (rows.length <= 1) {
                console.log("등록할 장소 데이터가 없습니다.");
                return;
            }

            if (!metaData) {
                console.log("지도 정보를 불러오지 못했습니다.");
                return;
            }

            const data = convertRowsToPlaceProps(rows, metaData);

            const fileIdx = fileList.length;

            const newFile: FileListItem = {
                file,
                data,
            };

            setFileList((prev) => [...prev, newFile]);
            setFailedList((prev) => [...prev, {}]);

            setSelectedFileIdx(fileIdx);
            props.progress(0);
        } catch (error) {
            console.error(error);
            console.log("파일을 읽는 중 오류가 발생했습니다.");
        }
    };
    const geocoding = async (fileIdx: number) => {
        if (!metaData) {
            return;
        }

        const item = fileList[fileIdx];

        if (!item?.data) {
            return;
        }

        const { geoField, category, placeName } = metaData.metadata;

        const sourceData = item.data;

        const addresses = Object.entries(sourceData)
            .filter(([, row]) => row[geoField]?.trim())
            .map(([rowIndex, row]) => ({
                index: Number(rowIndex),
                query: row[geoField].trim(),
            }));

        if (addresses.length === 0) {
            console.log("주소가 입력된 데이터가 없습니다.");
            return;
        }

        props.progress(10);

        try {
            const response = await DataProvider.MultiFullGeocoding({
                addresses,
            });

            if (!response.flag || !Array.isArray(response.result)) {
                throw new Error("지오코딩 요청 실패");
            }

            props.progress(70);

            const coords: PlaceCoordsList = {};
            const successData: PlacePropsList = {};
            const failedData: PlacePropsList = {};

            response.result.forEach(
                (result: {
                    index: string;
                    data: {
                        address: string | null;
                        coords: number[] | null;
                        matchStatus: string;
                    } | null;
                }) => {
                    const rowIndex = Number(result.index);
                    const row = sourceData[rowIndex];

                    if (!row) {
                        return;
                    }

                    if (
                        result.data?.coords &&
                        result.data.matchStatus === "exact"
                    ) {
                        coords[rowIndex] = {
                            coords: result.data.coords,
                            category: category
                                ? row[category] ?? ""
                                : "",
                            label: row[placeName],
                        };

                        successData[rowIndex] = row;
                    } else {
                        failedData[rowIndex] = row;
                    }
                }
            );

            setFileList((prev) => {
                const next = [...prev];

                next[fileIdx] = {
                    ...next[fileIdx],
                    data: successData,
                    result: {
                        success: Object.keys(successData).length,
                        fail: Object.keys(failedData).length,
                    },
                };

                return next;
            });

            setFailedList((prev) => {
                const next = [...prev];
                next[fileIdx] = failedData;
                return next;
            });

            addResult(coords);

            props.progress(101);
        } catch (error) {
            console.error(error);

            setFileList((prev) => {
                const next = [...prev];

                next[fileIdx] = {
                    ...next[fileIdx],
                    result: {
                        success: 0,
                        fail: Object.keys(next[fileIdx].data ?? {}).length,
                    },
                };

                return next;
            });

            props.progress(-1);

            console.log("주소 변환 중 오류가 발생했습니다.");
        }
    };

    const isCategoryLimitExceeded = () => {
        if (!metaData) {
            return false;
        }

        const categoryField =
            metaData.metadata.category;

        if (!categoryField) {
            return false;
        }

        const categories = new Set<string>();

        props.categories.forEach((category) => {
            if (category.label) {
                categories.add(
                    category.label.trim()
                );
            }
        });

        fileList.forEach((file) => {
            if (!file.data) {
                return;
            }

            Object.values(file.data).forEach(
                (row) => {
                    const value =
                        row[
                            categoryField
                        ]?.trim();

                    if (value) {
                        categories.add(value);
                    }
                }
            );
        });

        return categories.size > 16;
    };
    // hmchoi **********************************************************************************

    return (
        <>
            <ModalComponent />
            <div className={`${styles.wrapper} undraggable`}>
                <ContentTitle title={"신규 장소 일괄 등록"} />
                <div className={styles.container}>
                    <div className={styles.top}>
                        <div className={styles.content}>
                            <span className={styles.title}>{"파일 등록"}</span>
                            <span className={styles.desc}>{" 📍 엑셀 혹은 csv 파일을 업로드하여 여러 장소를 한 번에 등록하세요."}</span>
                            <div
                                className={`${styles.dropzone} ${dragState ? styles.active : ""}`}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrag}
                            >
                                <Icon name="uploadFile" size="xlarge" />
                                <div className={styles.desc}>
                                    <span>{"엑셀 및 CSV 파일을 드래그하여 추가하세요."}</span>
                                    <span>{"여러 파일을 추가하면 데이터가 누적됩니다."}</span>
                                </div>
                                <div className={styles.buttons}>
                                    <MiniButton label={"형식 다운로드"} onClick={downloadForm} />
                                    <MiniButton label={"도움말"} onClick={openGuide} />
                                </div>
                            </div>
                        </div>
                        {fileList[selectedFileIdx] && (
                            <div className={styles.list}>
                                <span className={styles.title}>{"파일별 등록 결과"}</span>
                                <div className={styles.file_list}>
                                    {fileList.map((item: FileListItem, idx: number) => {
                                        const isActive = idx === selectedFileIdx;

                                        const deleteFile = () => {
                                            const _fileList = [...fileList];
                                            const _failedList = [...failedList];

                                            _fileList.splice(idx, 1);
                                            _failedList.splice(idx, 1);

                                            setFileList(_fileList);
                                            setFailedList(_failedList);
                                            deleteResult(idx, true);

                                            setSelectedFileIdx(_fileList.length - 1);
                                        };

                                        const selectFile = () => {
                                            setSelectedFileIdx(idx);
                                        };

                                        return (
                                            <AddPlacesFileItem
                                                key={idx}
                                                isActive={isActive}
                                                item={item}
                                                fieldName={metaData?.metadata.fieldName}
                                                failedData={item.result ? failedList[idx] : undefined}
                                                onClick={selectFile}
                                                onClickDelete={props.progress() > -1 && props.progress() < 100 ? undefined : deleteFile}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {props.progress() > 100 && renderResult()}
                    </div>
                </div>
                <div className={styles.bottom}>
                    <BottomButtonBox
                        left={{ label: "이전", type: MiniButtonType.GRAY, onClick: cancel }}
                        right={{
                            label: "확인",
                            type: validation() ? MiniButtonType.BLUE : MiniButtonType.DISABLED,
                            onClick: validation() ? save : undefined,
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default AddMultiplePlaces;
